const PRIMARY_FLOWMAP_COLORS = [
    '#FF6779',
    '#194C7A',
    '#E18138',
    '#E15E1F',
    '#22617F',
    '#BA7676',
    '#FF5468',
    '#1BAE70',
];

function hexToRgb01(hex) {
    hex = hex.replace('#', '');
    return [
        parseInt(hex.substring(0, 2), 16) / 255,
        parseInt(hex.substring(2, 4), 16) / 255,
        parseInt(hex.substring(4, 6), 16) / 255,
    ];
}

function rgb01ToHex(r, g, b) {
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v * 255)));
    return (
        '#' +
        [r, g, b]
            .map(clamp)
            .map((v) => v.toString(16).padStart(2, '0').toUpperCase())
            .join('')
    );
}

// Convert RGB [0-1] to HLS (matching Python's colorsys convention)
function rgbToHls(r, g, b) {
    const maxc = Math.max(r, g, b);
    const minc = Math.min(r, g, b);
    const l = (maxc + minc) / 2;

    if (maxc === minc) return [0, l, 0];

    const d = maxc - minc;
    const s = l <= 0.5 ? d / (maxc + minc) : d / (2 - maxc - minc);

    let h;
    if (r === maxc) h = (g - b) / d + (g < b ? 6 : 0);
    else if (g === maxc) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;

    return [h, l, s];
}

// Convert HLS to RGB (matching Python's colorsys convention)
function hlsToRgb(h, l, s) {
    if (s === 0) return [l, l, l];

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    const hue2rgb = (t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    return [hue2rgb(h + 1 / 3), hue2rgb(h), hue2rgb(h - 1 / 3)];
}

function adjustHls(hex, deltaH = 0, deltaL = 0, deltaS = 0) {
    const [r, g, b] = hexToRgb01(hex);
    let [h, l, s] = rgbToHls(r, g, b);
    h = (((h + deltaH) % 1) + 1) % 1;
    l = Math.max(0, Math.min(1, l + deltaL));
    s = Math.max(0, Math.min(1, s + deltaS));
    const [r2, g2, b2] = hlsToRgb(h, l, s);
    return rgb01ToHex(r2, g2, b2);
}

function spacedOffsets(n, maxDelta) {
    if (n <= 1) return [0];
    const step = (2 * maxDelta) / (n - 1);
    return Array.from({ length: n }, (_, i) => -maxDelta + i * step);
}

export function applyFlowmapColors(
    allData,
    {
        baseCountryKey = 'startCountryIso',
        maxLightnessDelta = -0.28,
        maxHueDelta = 0.02,
        productSDelta = 0,
    } = {}
) {
    // Sort by country for deterministic color assignment
    const sortedForCountries = [...allData].sort((a, b) => {
        const ca = a[baseCountryKey] || '';
        const cb = b[baseCountryKey] || '';
        return ca.localeCompare(cb);
    });

    // Base color per country
    const countryColorMap = {};
    const countriesInOrder = [];
    for (const item of sortedForCountries) {
        const c =
            item[baseCountryKey] || item.endCountryIso || item.startCountryIso;
        if (c && !countryColorMap[c]) {
            countriesInOrder.push(c);
            countryColorMap[c] =
                PRIMARY_FLOWMAP_COLORS[
                    countriesInOrder.length % PRIMARY_FLOWMAP_COLORS.length ===
                    0
                        ? PRIMARY_FLOWMAP_COLORS.length - 1
                        : (countriesInOrder.length %
                              PRIMARY_FLOWMAP_COLORS.length) -
                          1
                ];
        }
    }

    // Lightness per dataType
    const dtypeKeys = [
        ...new Set(allData.map((i) => i.dataType).filter(Boolean)),
    ].sort();
    const dtypeLightnessOffsets = spacedOffsets(
        dtypeKeys.length,
        maxLightnessDelta
    );
    const datatypeShadeMap = {};
    dtypeKeys.forEach((k, i) => {
        datatypeShadeMap[k] = dtypeLightnessOffsets[i];
    });

    // Hue per hsProductName inside each (country, dataType)
    const bucketProducts = {};
    for (const item of allData) {
        const country =
            item[baseCountryKey] || item.endCountryIso || item.startCountryIso;
        const dtype = item.dataType;
        const pname = item.hsProductName;
        if (country && dtype && pname) {
            const key = `${country}|${dtype}`;
            if (!bucketProducts[key]) bucketProducts[key] = new Set();
            bucketProducts[key].add(pname);
        }
    }

    const productHueMap = {};
    for (const [key, namesSet] of Object.entries(bucketProducts)) {
        const uniq = [...namesSet].sort();
        const hueOffsets = spacedOffsets(uniq.length, maxHueDelta);
        productHueMap[key] = {};
        uniq.forEach((name, i) => {
            productHueMap[key][name] = hueOffsets[i];
        });
    }

    // Apply combined adjustments
    for (const item of allData) {
        const country =
            item[baseCountryKey] || item.endCountryIso || item.startCountryIso;
        const dtype = item.dataType;
        const pname = item.hsProductName;

        const baseHex = countryColorMap[country] || PRIMARY_FLOWMAP_COLORS[0];
        const dL = datatypeShadeMap[dtype] || 0;

        let dH = 0;
        if (country && dtype && pname) {
            const key = `${country}|${dtype}`;
            dH = (productHueMap[key] || {})[pname] || 0;
        }

        item.lineColor = adjustHls(baseHex, dH, dL, productSDelta);
    }

    return allData;
}
